/**
 * Featured Collections Section JavaScript
 * Handles tab switching on desktop
 */

class FeaturedCollections {
  constructor(element) {
    this.element = element;
    this.tabs = this.element.querySelectorAll('.featured-collections__tab');
    this.panels = this.element.querySelectorAll('.featured-collections__panel');
    this.overviews = this.element.querySelectorAll('.featured-collections__overview');
    this.init();
  }

  init() {
    if (this.tabs.length === 0 || this.panels.length === 0) return;

    // Add click listeners to tabs
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.switchTab(index));
      
      // Keyboard navigation
      tab.addEventListener('keydown', (e) => this.handleKeyboard(e, index));
    });

    // Set initial active state
    this.setActiveTab(0);
  }

  switchTab(index) {
    if (index < 0 || index >= this.tabs.length) return;

    this.setActiveTab(index);
  }

  setActiveTab(index) {
    // Remove active state from all tabs, panels, and overviews
    this.tabs.forEach(tab => {
      tab.classList.remove('featured-collections__tab--active');
      tab.setAttribute('aria-selected', 'false');
      tab.setAttribute('tabindex', '-1');
    });

    this.panels.forEach(panel => {
      panel.classList.remove('featured-collections__panel--active');
    });

    this.overviews.forEach(overview => {
      overview.classList.remove('featured-collections__overview--active');
    });

    // Add active state to selected tab, panel, and overview
    if (this.tabs[index]) {
      this.tabs[index].classList.add('featured-collections__tab--active');
      this.tabs[index].setAttribute('aria-selected', 'true');
      this.tabs[index].setAttribute('tabindex', '0');
    }
    
    if (this.panels[index]) {
      this.panels[index].classList.add('featured-collections__panel--active');
    }

    if (this.overviews[index]) {
      this.overviews[index].classList.add('featured-collections__overview--active');
    }

    // Announce to screen readers
    this.announceTabChange(index);
  }

  handleKeyboard(e, currentIndex) {
    let newIndex = currentIndex;

    switch (e.key) {
      case 'ArrowLeft':
        e.preventDefault();
        newIndex = currentIndex > 0 ? currentIndex - 1 : this.tabs.length - 1;
        break;
      case 'ArrowRight':
        e.preventDefault();
        newIndex = currentIndex < this.tabs.length - 1 ? currentIndex + 1 : 0;
        break;
      case 'Home':
        e.preventDefault();
        newIndex = 0;
        break;
      case 'End':
        e.preventDefault();
        newIndex = this.tabs.length - 1;
        break;
      default:
        return;
    }

    this.switchTab(newIndex);
    this.tabs[newIndex].focus();
  }

  announceTabChange(index) {
    const tabText = this.tabs[index].querySelector('.featured-collections__tab-label-text');
    if (tabText) {
      const announcement = document.createElement('div');
      announcement.setAttribute('role', 'status');
      announcement.setAttribute('aria-live', 'polite');
      announcement.className = 'sr-only';
      announcement.textContent = `${tabText.textContent} tab selected`;
      
      document.body.appendChild(announcement);
      
      setTimeout(() => {
        document.body.removeChild(announcement);
      }, 1000);
    }
  }
}

// Initialize all featured collections sections
function initFeaturedCollections() {
  const sections = document.querySelectorAll('.featured-collections');
  sections.forEach(section => {
    new FeaturedCollections(section);
  });
}

// Initialize on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initFeaturedCollections);
} else {
  initFeaturedCollections();
}

// Re-initialize when Shopify section is updated in the theme editor
if (Shopify && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    if (event.target.classList.contains('featured-collections') || 
        event.target.querySelector('.featured-collections')) {
      initFeaturedCollections();
    }
  });
}

// Export for potential use in other scripts
if (typeof module !== 'undefined' && module.exports) {
  module.exports = FeaturedCollections;
}
